/*
  Hero diaporama — self-contained crossfade slideshow.
  Kept out of the section file so the browser caches it once instead of
  re-parsing it inline on every page render. Fully generic: it binds to
  any [data-nw-diaporama] root, so it needs no Liquid interpolation.
*/
(function () {
  var INTERNAL_FIRST_TOUCH_KEY = 'nawhals_internal_first_touch';
  var INTERNAL_LAST_TOUCH_KEY = 'nawhals_internal_last_touch';
  var CART_FIRST_TOUCH_ATTRIBUTE = '_nawhals_internal_first_touch';
  var CART_LAST_TOUCH_ATTRIBUTE = '_nawhals_internal_last_touch';
  var INTERNAL_TOUCH_TTL_MS = 30 * 24 * 60 * 60 * 1000;
  var lastRequestedCartTouchSignature = null;
  var INTERNAL_TOUCH_FIELDS = [
    'tracking_id',
    'position',
    'label',
    'slide_title',
    'page_path',
    'destination',
    'timestamp'
  ];

  function analyticsProcessingAllowed() {
    try {
      var customerPrivacy = window.Shopify && window.Shopify.customerPrivacy;
      return !!customerPrivacy
        && typeof customerPrivacy.analyticsProcessingAllowed === 'function'
        && customerPrivacy.analyticsProcessingAllowed() === true;
    } catch (error) {
      return false;
    }
  }

  function removeStoredInternalTouch(key) {
    try {
      window.localStorage.removeItem(key);
    } catch (error) {
      /* Storage unavailable - tracking must never affect the CTA. */
    }
  }

  function isStringOrNull(value) {
    return value === null || typeof value === 'string';
  }

  function isValidInternalTouch(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;

    var hasEveryField = INTERNAL_TOUCH_FIELDS.every(function (field) {
      return Object.prototype.hasOwnProperty.call(value, field);
    });

    return hasEveryField
      && typeof value.tracking_id === 'string'
      && value.tracking_id.trim() !== ''
      && isStringOrNull(value.position)
      && isStringOrNull(value.label)
      && isStringOrNull(value.slide_title)
      && typeof value.page_path === 'string'
      && typeof value.destination === 'string'
      && typeof value.timestamp === 'string'
      && !isNaN(Date.parse(value.timestamp));
  }

  function readStoredInternalTouch(key) {
    try {
      var raw = window.localStorage.getItem(key);
      if (!raw) return null;

      var value = JSON.parse(raw);
      if (isValidInternalTouch(value)) return value;
    } catch (error) {
      /* Invalid JSON or unavailable storage is handled by cleanup below. */
    }

    removeStoredInternalTouch(key);
    return null;
  }

  function writeStoredInternalTouch(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      /* Private mode, quota or disabled storage - navigation continues. */
    }
  }

  function clearStoredInternalTouches() {
    removeStoredInternalTouch(INTERNAL_FIRST_TOUCH_KEY);
    removeStoredInternalTouch(INTERNAL_LAST_TOUCH_KEY);
  }

  function internalTouchExpired(touch) {
    return Date.now() - Date.parse(touch.timestamp) > INTERNAL_TOUCH_TTL_MS;
  }

  function internalCtaDestination(cta) {
    try {
      var destination = new URL(cta.href, window.location.href);
      return destination.origin === window.location.origin
        ? destination.pathname
        : destination.href;
    } catch (error) {
      return cta.getAttribute('href') || '';
    }
  }

  function buildInternalTouch(cta) {
    return {
      tracking_id: cta.dataset.trackingId,
      position: cta.dataset.ctaPosition || null,
      label: cta.dataset.ctaLabel || null,
      slide_title: cta.dataset.ctaSlideTitle || null,
      page_path: window.location.pathname,
      destination: internalCtaDestination(cta),
      timestamp: new Date().toISOString()
    };
  }

  function rememberInternalTouch(touch) {
    var firstTouch = readStoredInternalTouch(INTERNAL_FIRST_TOUCH_KEY);
    readStoredInternalTouch(INTERNAL_LAST_TOUCH_KEY);

    if (firstTouch && internalTouchExpired(firstTouch)) {
      clearStoredInternalTouches();
      firstTouch = null;
    }

    if (!firstTouch) {
      firstTouch = touch;
      writeStoredInternalTouch(INTERNAL_FIRST_TOUCH_KEY, touch);
    }

    writeStoredInternalTouch(INTERNAL_LAST_TOUCH_KEY, touch);

    return {
      firstTouch: firstTouch,
      lastTouch: touch
    };
  }

  function syncInternalTouchesToCart(firstTouch, lastTouch) {
    if (!firstTouch || !lastTouch || !window.fetch) return;

    var firstTrackingId = firstTouch.tracking_id;
    var lastTrackingId = lastTouch.tracking_id;
    if (!firstTrackingId || !lastTrackingId) return;

    var signature = firstTrackingId + '\n' + lastTrackingId;
    if (signature === lastRequestedCartTouchSignature) return;

    var routesRoot = window.Shopify
      && window.Shopify.routes
      && window.Shopify.routes.root;
    if (!routesRoot) return;

    lastRequestedCartTouchSignature = signature;

    var attributes = {};
    attributes[CART_FIRST_TOUCH_ATTRIBUTE] = firstTrackingId;
    attributes[CART_LAST_TOUCH_ATTRIBUTE] = lastTrackingId;

    try {
      window.fetch(routesRoot + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'same-origin',
        keepalive: true,
        body: JSON.stringify({ attributes: attributes })
      }).then(function (response) {
        if (!response.ok) throw new Error('Cart attribute sync failed');
      }).catch(function () {
        lastRequestedCartTouchSignature = null;
      });
    } catch (error) {
      lastRequestedCartTouchSignature = null;
    }
  }

  function dispatchInternalCtaClick(touch) {
    try {
      window.dispatchEvent(new CustomEvent('nawhals_cta_click', {
        detail: touch
      }));
    } catch (error) {
      /* An internal observer must never affect navigation. */
    }
  }

  function trackInternalCtaClick(cta) {
    if (!cta || !cta.matches('[data-nw-cta][data-tracking-id]')) return;
    if (!analyticsProcessingAllowed()) return;

    var touch = buildInternalTouch(cta);
    if (!touch.tracking_id) return;

    var rememberedTouches = rememberInternalTouch(touch);
    syncInternalTouchesToCart(rememberedTouches.firstTouch, rememberedTouches.lastTouch);
    dispatchInternalCtaClick(touch);
  }

  function initDiaporama(root) {
    if (root.dataset.nwInitialized === 'true') return;
    root.dataset.nwInitialized = 'true';

    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-nw-slide]'));
    var copies = Array.prototype.slice.call(root.querySelectorAll('[data-nw-copy]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-nw-dot]'));
    var prevBtn = root.querySelector('[data-nw-prev]');
    var nextBtn = root.querySelector('[data-nw-next]');
    var total = slides.length;
    if (total === 0) return;

    var current = 0;
    var autoplay = root.getAttribute('data-autoplay') === 'true';
    var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) autoplay = false;
    var speed = parseInt(root.getAttribute('data-speed'), 10) || 5000;
    var timeoutId = null;
    var startedAt = null;
    var remaining = speed;
    var paused = false;
    var touchStartX = null;
    var touchStartY = null;

    function dotFill(index) {
      return dots[index] ? dots[index].querySelector('[data-nw-dot-fill]') : null;
    }

    function clearFills() {
      dots.forEach(function (dot) {
        var fill = dot.querySelector('[data-nw-dot-fill]');
        if (!fill) return;
        fill.style.animation = 'none';
        fill.style.transform = 'scaleX(0)';
      });
    }

    function startFill(duration) {
      clearFills();
      var fill = dotFill(current);
      if (!fill) return;
      fill.style.animation = 'none';
      void fill.offsetWidth;
      fill.style.animation = 'nw-diaporama-fill ' + duration + 'ms linear forwards';
      fill.style.animationPlayState = 'running';
    }

    function resetReveal(slide) {
      if (!slide) return;
      slide.querySelectorAll('animate-element').forEach(function (node) {
        if (typeof node.reset === 'function') node.reset();
      });
    }

    function refreshReveal(slide) {
      if (!slide) return;
      slide.querySelectorAll('animate-element').forEach(function (node) {
        if (typeof node.refresh === 'function') node.refresh();
      });
    }

    function goTo(index) {
      index = (index + total) % total;
      if (index === current) return;
      var previous = slides[current];
      var previousCopy = copies[current];
      previous.classList.remove('is-active');
      previous.setAttribute('aria-hidden', 'true');
      if (previousCopy) previousCopy.classList.remove('is-active');
      if (previousCopy) previousCopy.setAttribute('aria-hidden', 'true');
      if (dots[current]) {
        dots[current].classList.remove('is-active');
        dots[current].removeAttribute('aria-current');
      }
      current = index;
      slides[current].classList.add('is-active');
      slides[current].setAttribute('aria-hidden', 'false');
      if (copies[current]) copies[current].classList.add('is-active');
      if (copies[current]) copies[current].setAttribute('aria-hidden', 'false');
      if (dots[current]) {
        dots[current].classList.add('is-active');
        dots[current].setAttribute('aria-current', 'true');
      }
      resetReveal(previousCopy);
      refreshReveal(copies[current]);
    }

    function next() { goTo(current + 1); }
    function prev() { goTo(current - 1); }

    function clearTimer() {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    }

    function armTimer(duration) {
      clearTimer();
      startedAt = Date.now();
      remaining = duration;
      timeoutId = setTimeout(function () {
        next();
        play();
      }, duration);
    }

    function play() {
      clearTimer();
      paused = false;
      if (total > 1 && autoplay) {
        armTimer(speed);
        startFill(speed);
      } else {
        clearFills();
      }
    }

    function pause() {
      if (!autoplay || total < 2 || paused) return;
      paused = true;
      remaining -= (Date.now() - startedAt);
      if (remaining < 0) remaining = 0;
      clearTimer();
      var fill = dotFill(current);
      if (fill) fill.style.animationPlayState = 'paused';
    }

    function resume() {
      if (!autoplay || total < 2 || !paused) return;
      paused = false;
      armTimer(remaining);
      var fill = dotFill(current);
      if (fill) fill.style.animationPlayState = 'running';
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { prev(); play(); });
    if (nextBtn) nextBtn.addEventListener('click', function () { next(); play(); });
    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { goTo(i); play(); });
    });

    var swipeSurface = root.querySelector('.nw-diaporama');
    if (swipeSurface) {
      swipeSurface.addEventListener('touchstart', function (event) {
        if (event.touches.length !== 1) return;
        touchStartX = event.touches[0].clientX;
        touchStartY = event.touches[0].clientY;
      }, { passive: true });

      swipeSurface.addEventListener('touchend', function (event) {
        if (touchStartX === null || touchStartY === null || event.changedTouches.length !== 1) return;
        var deltaX = event.changedTouches[0].clientX - touchStartX;
        var deltaY = event.changedTouches[0].clientY - touchStartY;
        touchStartX = null;
        touchStartY = null;
        if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
        if (deltaX < 0) next();
        else prev();
        play();
      }, { passive: true });

      swipeSurface.addEventListener('touchcancel', function () {
        touchStartX = null;
        touchStartY = null;
      }, { passive: true });
    }

    // The theme reports --header-height as 0px, so the sticky/transparent
    // header would sit on top of the copy. Measure what the header actually
    // covers of this section and expose it as --nw-header-clearance.
    function syncHeaderClearance() {
      var section = root.closest('[id^="shopify-section"]');
      var header = document.querySelector('.header');
      if (!section || !header) return;
      var position = window.getComputedStyle(header).position;
      var clearance = 0;
      if (position === 'absolute' || position === 'fixed') {
        if (window.pageYOffset < 4) {
          var headerRect = header.getBoundingClientRect();
          clearance = Math.max(0, headerRect.top + headerRect.height - root.getBoundingClientRect().top);
        } else {
          clearance = header.offsetHeight;
        }
      }
      section.style.setProperty('--nw-header-clearance', Math.round(clearance) + 'px');
    }

    syncHeaderClearance();
    window.addEventListener('resize', syncHeaderClearance);
    window.addEventListener('load', syncHeaderClearance);

    root.addEventListener('mouseenter', pause);
    root.addEventListener('mouseleave', resume);
    root.addEventListener('focusin', pause);
    root.addEventListener('focusout', function (event) {
      if (!root.contains(event.relatedTarget)) resume();
    });
    root.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        prev();
        play();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        next();
        play();
      }
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) pause();
      else resume();
    });

    root.querySelectorAll('[data-nw-cta]').forEach(function (cta) {
      cta.addEventListener('click', function (event) {
        trackInternalCtaClick(cta);

        var trackingId = cta.dataset.trackingId || '';
        var creativeName = cta.getAttribute('data-cta-label') || '';
        var creativeSlot = 'hero_diaporama_button_' + (cta.getAttribute('data-cta-position') || '');
        var promotionName = cta.getAttribute('data-cta-slide-title') || creativeName;

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'select_promotion',
          ecommerce: {
            creative_name: creativeName,
            creative_slot: creativeSlot,
            promotion_id: trackingId,
            promotion_name: promotionName,
            location_id: 'hero_diaporama',
            items: []
          },
          utm_campaign: cta.getAttribute('data-cta-utm-campaign') || '',
          utm_content: cta.getAttribute('data-cta-utm-content') || ''
        });

        if (analyticsProcessingAllowed() && typeof window.gtag === 'function') {
          var ga4Parameters = {
            send_to: 'G-WFR9S4DN69',
            creative_name: creativeName,
            creative_slot: creativeSlot,
            promotion_id: trackingId,
            promotion_name: promotionName,
            location_id: 'hero_diaporama'
          };
          var target = (cta.getAttribute('target') || '').toLowerCase();
          var shouldDelayNavigation = cta.matches('[data-nw-cta][data-tracking-id]')
            && event.button === 0
            && !event.metaKey
            && !event.ctrlKey
            && !event.shiftKey
            && !event.altKey
            && !event.defaultPrevented
            && (target === '' || target === '_self')
            && !!cta.href;
          var continueNavigation = null;

          if (shouldDelayNavigation) {
            var originalHref = cta.href;
            var navigationStarted = false;
            var fallbackTimeoutId = null;

            continueNavigation = function () {
              if (navigationStarted) return;
              navigationStarted = true;
              if (fallbackTimeoutId !== null) window.clearTimeout(fallbackTimeoutId);
              window.location.assign(originalHref);
            };

            event.preventDefault();
            ga4Parameters.event_callback = continueNavigation;
            ga4Parameters.event_timeout = 400;
            fallbackTimeoutId = window.setTimeout(continueNavigation, 400);
          }

          try {
            window.gtag('event', 'select_promotion', ga4Parameters);
          } catch (error) {
            if (continueNavigation) continueNavigation();
          }
        }
      });
    });

    if (window.Shopify && window.Shopify.designMode) {
      root.addEventListener('shopify:block:select', function (event) {
        var index = slides.findIndex(function (slide) { return slide === event.target; });
        if (index > -1) goTo(index);
        clearTimer();
        clearFills();
      });
      root.addEventListener('shopify:block:deselect', play);
    }

    root.setAttribute('tabindex', '0');
    root.setAttribute('role', 'region');
    slides.forEach(function (slide, index) {
      slide.setAttribute('aria-hidden', index === current ? 'false' : 'true');
    });
    copies.forEach(function (copy, index) {
      copy.setAttribute('aria-hidden', index === current ? 'false' : 'true');
    });
    dots.forEach(function (dot, index) {
      if (index === current) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    refreshReveal(copies[current]);
    play();
  }

  function init() {
    document.querySelectorAll('[data-nw-diaporama]').forEach(initDiaporama);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-nw-diaporama]');
    if (root) initDiaporama(root);
  });
})();
